import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  selectInstructorId,
  selectIsOrgOwnerOrAdmin,
} from "../../authentication/authenticationSlice";
import {
  selectActiveColleagues,
  selectColleagues,
  selectColleaguesInitializationStatus,
} from "../instructors-slice";
import { fetchColleagues } from "../slice-extensions/instructors-slice-extension";

interface InstructorSelectProps {
  /** The currently assigned instructor id; undefined = the signed-in instructor. */
  value?: string;
  onChange: (instructorId: string) => void;
  disabled?: boolean;
}

/**
 * "Instructor" assignment select for the session form. Renders only when the
 * organization has more than one active colleague AND the viewer is Owner/Admin
 * (plain Instructors can't assign sessions to someone else).
 */
const InstructorSelect: React.FC<InstructorSelectProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isOrgOwnerOrAdmin = useAppSelector(selectIsOrgOwnerOrAdmin);
  const viewerInstructorId = useAppSelector(selectInstructorId);
  const colleagues = useAppSelector(selectColleagues);
  const activeColleagues = useAppSelector(selectActiveColleagues);
  const initStatus = useAppSelector(selectColleaguesInitializationStatus);

  useEffect(() => {
    if (initStatus === "not-initialized") {
      dispatch(fetchColleagues());
    }
  }, [dispatch, initStatus]);

  if (!isOrgOwnerOrAdmin || activeColleagues.length <= 1) {
    return null;
  }

  const selectedId = value ?? viewerInstructorId;
  // A session may be assigned to a since-deactivated colleague — keep them
  // selectable so opening the edit form doesn't silently reassign the session.
  const assignedInactive = colleagues.find(
    (c) => c.id === selectedId && !c.isActive
  );
  const options = assignedInactive
    ? [...activeColleagues, assignedInactive]
    : activeColleagues;

  return (
    <div className="w-full">
      <label htmlFor="session_instructor" className="label">
        {/* .label is layout-only — .label-text carries the theme-aware color. */}
        <p className="label-text">{t("instructors.label", "Instructor")}</p>
      </label>
      <select
        id="session_instructor"
        name="session_instructor"
        className="input w-full"
        value={selectedId}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {options.map((instructor) => (
          <option key={instructor.id} value={instructor.id}>
            {instructor.fullName}
            {!instructor.isActive ? " (inactive)" : ""}
          </option>
        ))}
      </select>
    </div>
  );
};

export default InstructorSelect;
